import React from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useParams, useNavigate } from 'react-router-dom';
import useWizardStep from '../../components/shared/useWizardStep';
import WizardPage from '../shared/WizardPage';
import useAsyncTask from '../shared/useAsyncTask';
import useNextResource from '../shared/useNextResource';
import { saveScale, figureListPath } from '../shared/wizardUtils';
import {
  FIGURE_FOR_EDIT_QUERY,
  NEXT_FIGURE_QUERY,
  SAM_CONTOUR_QUERY,
  SITES_QUERY,
  TAGS_QUERY
} from '../../graphql/queries';
import {
  UPDATE_FIGURE,
  UPDATE_FIGURE_TAGS,
  UPDATE_FIGURE_SCALE,
  UPDATE_SCALE_CONTOUR
} from '../../graphql/mutations';
import SizeFigureWizard from './SizeFigureWizard';

export default function SizeFigureEditPage() {
  const { id, figure_type: figureType } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useWizardStep('data');
  const task = useAsyncTask();

  const {
    data: figureData,
    loading: queryLoading,
    error: queryError
  } = useQuery(FIGURE_FOR_EDIT_QUERY, {
    variables: { id },
    skip: !id
  });
  const { data: sitesData } = useQuery(SITES_QUERY);
  const { data: tagsData } = useQuery(TAGS_QUERY);

  const refetch = {
    refetchQueries: [{ query: FIGURE_FOR_EDIT_QUERY, variables: { id } }],
    awaitRefetchQueries: true
  };
  const [updateFigure] = useMutation(UPDATE_FIGURE, refetch);
  const [updateFigureTags] = useMutation(UPDATE_FIGURE_TAGS, refetch);
  const [updateFigureScale] = useMutation(UPDATE_FIGURE_SCALE, refetch);
  const [updateScaleContour] = useMutation(UPDATE_SCALE_CONTOUR, refetch);
  const [runSamContour] = useLazyQuery(SAM_CONTOUR_QUERY, refetch);

  const figure = figureData?.figure;
  const goNextFigure = useNextResource({
    query: NEXT_FIGURE_QUERY,
    variables: { id, publicationId: figure?.publicationId, type: figure?.type?.toLowerCase() },
    nextKey: 'nextFigure',
    pathFor: (next) => `/figures/${figureType}/${next.id}/edit`,
    fallbackPath: figureListPath(figureType)
  });

  const handleUpdateFigure = (data) =>
    task.run(() => updateFigure({ variables: { input: { id, ...data } } }));
  const handleUpdateTags = (tagIds) =>
    task.run(() => updateFigureTags({ variables: { input: { id, tagIds } } }));
  const handleUpdateFigures = (inputs) =>
    task.run(() => Promise.all(inputs.map((input) => updateFigure({ variables: { input } }))));

  const handleUpdateScale = (data) =>
    task.run(
      () => saveScale({ data, resourceId: id, updateScale: updateFigureScale, updateFigure }),
      'Failed to update scale'
    );
  // Must resolve to the updated contour (the SAM selector awaits it).
  const handleUpdateScaleContour = (scaleId, points) =>
    updateScaleContour({ variables: { id: scaleId, points } }).then(
      (result) => result.data.updateScaleContour.contour
    );
  const handleUpdateContour = (figureId, points) =>
    runSamContour({ variables: { id: figureId, points } }).then(
      (result) => result.data.samContour.contour
    );

  return (
    <WizardPage
      breadcrumb={
        figure
          ? [
              { label: `${figureType}s`, link: figureListPath(figureType) },
              { label: figure.identifier || `Figure ${id}` }
            ]
          : []
      }
      loading={queryLoading}
      error={queryError}
      resource={figure}
      notFoundLabel="Figure not found"
      task={task}
    >
      <SizeFigureWizard
        step={step}
        setStep={setStep}
        figure={figure}
        sites={sitesData?.sites || []}
        tags={tagsData?.tags || []}
        onUpdateFigure={handleUpdateFigure}
        onUpdateTags={handleUpdateTags}
        onUpdateFigures={handleUpdateFigures}
        onUpdateScale={handleUpdateScale}
        onUpdateScaleContour={handleUpdateScaleContour}
        onUpdateContour={handleUpdateContour}
        onCancel={() => navigate(-1)}
        onFinish={goNextFigure}
        loading={task.busy || queryLoading}
      />
    </WizardPage>
  );
}
